import React, { useState, useEffect, useCallback } from "react";
import { adminApi } from "../services/api";
import { useToast } from "../components/Toast";
import useConfirm from "../hooks/useConfirm";
import PlanTable from "../components/admin/PlanTable";
import PlanForm from "../components/admin/PlanForm";
import InviteManager from "../components/admin/InviteManager";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../components/LoadingSpinner";
import "./AdminPlans.css";

/**
 * Admin page for managing subscription plans.
 * Allows creating, editing, activating/deactivating, and deleting plans.
 *
 * Role check is handled by ProtectedRoute (requiredRole="admin") in App.js.
 * Server enforces via requireRole("admin") middleware.
 */
const AdminPlans = () => {
  const toast = useToast();
  const { confirmState, confirm } = useConfirm();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Loading indicator for status toggle button
  const [togglingId, setTogglingId] = useState(null);

  // True during background refreshes (after mutations); distinct from initial load
  const [refreshing, setRefreshing] = useState(false);

  // Invite manager modal state
  const [invitesPlan, setInvitesPlan] = useState(null);

  const fetchPlans = useCallback(async ({ isRefresh = false } = {}) => {
    try {
      setError(null);
      if (isRefresh) setRefreshing(true);
      const data = await adminApi.getPlans();
      setPlans(data.plans);
    } catch (err) {
      const status = err.response?.status;
      if (status === 403 || status === 401) {
        setError("Access denied. You do not have permission to manage plans.");
      } else {
        setError(err.response?.data?.message || "Failed to load plans");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleCreateClick = () => {
    setEditingPlan(null);
    setFormOpen(true);
  };

  const handleEditClick = (plan) => {
    setEditingPlan(plan);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingPlan(null);
  };

  const handleFormSubmit = async (payload, planId) => {
    setSubmitting(true);
    try {
      if (planId) {
        await adminApi.updatePlan(planId, payload);
        toast.success(`Plan "${payload.name}" updated`);
      } else {
        await adminApi.createPlan(payload);
        toast.success(`Plan "${payload.name}" created`);
      }
      handleFormClose();
      await fetchPlans({ isRefresh: true });
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to save plan",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleClick = async (planId, isActive) => {
    const plan = plans.find((p) => p.id === planId);
    const planName = plan?.name || planId;

    // When deactivating, fetch user count so admin sees who will be grandfathered
    let deactivateMessage;
    if (!isActive) {
      try {
        const data = await adminApi.getPlanUserCount(planId);
        deactivateMessage =
          data.userCount > 0
            ? `Deactivate the "${planName}" plan? ${data.userCount} existing user(s) will be grandfathered (keep their current plan), but no new signups will be allowed.`
            : `Deactivate the "${planName}" plan? No users are currently on this plan. No new signups will be allowed.`;
      } catch {
        deactivateMessage = `Deactivate the "${planName}" plan? (Could not determine current user count.) Existing users will keep their plan, but no new signups will be allowed.`;
      }
    }

    const confirmed = await confirm({
      title: isActive ? "Activate Plan" : "Deactivate Plan",
      message: isActive
        ? `Activate the "${planName}" plan? New users will be able to sign up for it.`
        : deactivateMessage,
      confirmText: isActive ? "Activate" : "Deactivate",
      variant: isActive ? "info" : "danger",
    });

    if (!confirmed) return;

    setTogglingId(planId);
    try {
      const result = await adminApi.togglePlanStatus(planId, isActive);
      if (result.message) {
        toast.info(result.message);
      } else {
        toast.success(`Plan ${isActive ? "activated" : "deactivated"}`);
      }
      await fetchPlans({ isRefresh: true });
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update plan status",
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleDeleteClick = async (plan) => {
    // Pre-check user count
    try {
      const data = await adminApi.getPlanUserCount(plan.id);
      if (data.userCount > 0) {
        toast.error(
          `Cannot delete "${plan.name}" — ${data.userCount} user(s) are on this plan. Deactivate it instead.`,
        );
        return;
      }
    } catch (err) {
      console.warn("Failed to pre-check user count:", err);
      // Proceed anyway — the delete endpoint will enforce the check server-side
    }

    const confirmed = await confirm({
      title: "Delete Plan",
      message: `Are you sure you want to delete the "${plan.name}" plan? This cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      await adminApi.deletePlan(plan.id);
      toast.success(`Plan "${plan.name}" deleted`);
      await fetchPlans({ isRefresh: true });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete plan");
    }
  };

  if (loading) {
    return (
      <div className="admin-plans-page">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="admin-plans-page">
      <div className="admin-plans-header">
        <div>
          <h2>Manage Plans</h2>
          <p className="admin-plans-subtitle">
            Create, edit, and manage subscription plans. Deactivating a plan
            grandfathers existing users — they keep their plan, but no new
            signups are allowed.
          </p>
        </div>
        <button
          className="admin-btn admin-btn-primary"
          onClick={handleCreateClick}
        >
          + Create Plan
        </button>
      </div>

      {refreshing && (
        <div className="admin-refreshing">Refreshing plans...</div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {plans.length > 0 ? (
        <PlanTable
          plans={plans}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onToggleStatus={handleToggleClick}
          onManageInvites={setInvitesPlan}
          togglingId={togglingId}
        />
      ) : (
        <p>No plans found. Create your first plan to get started.</p>
      )}

      <PlanForm
        isOpen={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        plan={editingPlan}
        isSubmitting={submitting}
        allPlans={plans}
      />

      {invitesPlan && (
        <InviteManager
          plan={invitesPlan}
          onClose={() => setInvitesPlan(null)}
        />
      )}

      <ConfirmDialog {...confirmState} />
    </div>
  );
};

export default AdminPlans;
